// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/hr/LeavePoliciesPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, ShieldAlert, X, Layers, AlertCircle, Eye, Calendar, Clock } from "lucide-react";
import { toast } from "react-hot-toast"; 
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import PageHeader from "../ui/PageHeader";
import ModernTable from "../ui/ModernTable";
import SelectDropdown from "../../customStyles/SelectDropdown";

const LeavePoliciesPage = () => {
  // --- State Management ---
  const [policies, setPolicies] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]); 
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false); 
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // --- Form State ---
  const [formData, setFormData] = useState({
    name: "",
    payrollCycleStartDay: 26,
    weekOffDays: [0], 
    isDefault: false,
    isActive: true,
    allocations: [],
    maxPermissionsPerMonth: 4,               // மாதாந்திர எண்ணிக்கை வரம்பு (Count)
    maxPermissionMinutesPerMonth: 240,        // மாதாந்திர மொத்த நிமிடங்கள் (Total Mins)
    maxPermissionMinutesPerRequest: 120       // 👈 ஒரு முறைக்கான அதிகபட்ச நிமிடங்கள் (Per Request Mins) - NO HARDCODING!
  });

  const [formErrors, setFormErrors] = useState({});

  const weekDays = [
    { label: "Sunday", id: 0 },
    { label: "Monday", id: 1 },
    { label: "Tuesday", id: 2 },
    { label: "Wednesday", id: 3 },
    { label: "Thursday", id: 4 },
    { label: "Friday", id: 5 },
    { label: "Saturday", id: 6 },
  ];

  const accrualTypes = [
    { label: "Upfront Credit", id: "upfront" },
    { label: "Monthly Accrual", id: "monthly" },
  ];

  const leaveTypesOptions = leaveTypes.map(type => ({
    label: `${type.name} (${type.code})`,
    id: type._id
  }));

  const fetchLeaveTypes = async () => {
    try {
      const url = SummaryApi.getAllLeaveTypes?.url || "/api/hr/leave-types/view-all";
      const method = SummaryApi.getAllLeaveTypes?.method || "get";
      const res = await api({ url, method });
      if (res.data.success) {
        setLeaveTypes(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching leave types:", err);
    }
  };

  const fetchLeavePolicies = async () => {
    setIsLoading(true);
    try {
      const url = SummaryApi.getAllLeavePolicies?.url || "/api/hr/leave-policies";
      const method = SummaryApi.getAllLeavePolicies?.method || "get";
      const res = await api({ url, method });

      if (res.data.success) {
        setPolicies(res.data.data || []);
      } else {
        toast.error(res.data.message || "Failed to load leave policies.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error loading leave policies.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
    fetchLeavePolicies();
  }, []);

  const addAllocationRow = () => {
    setFormData(prev => ({
      ...prev,
      allocations: [
        ...prev.allocations,
        { leaveTypeId: "", daysPerYear: 12, accrualType: "upfront" }
      ]
    }));
  };

  const removeAllocationRow = (index) => {
    setFormData(prev => ({
      ...prev,
      allocations: prev.allocations.filter((_, i) => i !== index)
    }));
  };

  const handleAllocationChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.allocations];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, allocations: updated };
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: val }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const openFormDrawer = (policy = null) => {
    if (policy) {
      setSelectedPolicy(policy);
      const mappedAllocations = (policy.allocations || []).map(alloc => ({
        leaveTypeId: alloc.leaveTypeId?._id || alloc.leaveTypeId,
        daysPerYear: alloc.daysPerYear,
        accrualType: alloc.accrualType || "upfront"
      }));

      setFormData({
        name: policy.name,
        payrollCycleStartDay: policy.payrollCycleStartDay || 26,
        weekOffDays: policy.weekOffDays || [0],
        isDefault: policy.isDefault || false,
        isActive: policy.isActive ?? true,
        allocations: mappedAllocations,
        maxPermissionsPerMonth: policy.maxPermissionsPerMonth ?? 4,               
        maxPermissionMinutesPerMonth: policy.maxPermissionMinutesPerMonth ?? 240,    
        maxPermissionMinutesPerRequest: policy.maxPermissionMinutesPerRequest ?? 120 // 👈 எடிட் செய்யும்போது
      });
    } else {
      setSelectedPolicy(null);
      setFormData({
        name: "",
        payrollCycleStartDay: 26,
        weekOffDays: [0],
        isDefault: false,
        isActive: true,
        allocations: [],
        maxPermissionsPerMonth: 4,               
        maxPermissionMinutesPerMonth: 240,        
        maxPermissionMinutesPerRequest: 120       // 👈 புதிதாக உருவாக்கும்போது
      });
    }
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeFormDrawer = () => {
    setIsFormOpen(false);
    setSelectedPolicy(null);
  };

  const openViewDrawer = (policy) => {
    setSelectedPolicy(policy);
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedPolicy(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Policy name is required";
    if (formData.payrollCycleStartDay < 1 || formData.payrollCycleStartDay > 31) {
      errors.payrollCycleStartDay = "Must be a valid day between 1 and 31";
    }
    if (formData.maxPermissionMinutesPerMonth < 1) {
      errors.maxPermissionMinutesPerMonth = "Must be a valid duration in minutes";
    }
    if (formData.maxPermissionMinutesPerRequest < 1) {
      errors.maxPermissionMinutesPerRequest = "Must be a valid duration in minutes";
    }
    if (formData.allocations.length === 0) {
      errors.allocations = "At least one leave type allocation is required";
    } else {
      const allocationErrors = [];
      formData.allocations.forEach((alloc, index) => {
        if (!alloc.leaveTypeId) {
          allocationErrors.push(`Row ${index + 1}: Leave Type is required`);
        }
        if (alloc.daysPerYear < 0) {
          allocationErrors.push(`Row ${index + 1}: Days cannot be negative`);
        }
      });
      if (allocationErrors.length > 0) {
        errors.allocations = allocationErrors.join(", ");
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let url, method;

      if (selectedPolicy) {
        url = SummaryApi.updateLeavePolicy?.url(selectedPolicy._id) || `/api/hr/leave-policies/${selectedPolicy._id}`;
        method = SummaryApi.updateLeavePolicy?.method || "put";
      } else {
        url = SummaryApi.createLeavePolicy?.url || "/api/hr/leave-policies";
        method = SummaryApi.createLeavePolicy?.method || "post";
      }

      const res = await api({
        url,
        method,
        data: formData,
      });

      if (res.data.success) {
        toast.success(res.data.message || `Leave policy ${selectedPolicy ? "updated" : "created"} successfully!`);
        await fetchLeavePolicies();
        closeFormDrawer();
      } else {
        toast.error(res.data.message || "Operation failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error submitting leave policy.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerDelete = (policy) => {
    setSelectedPolicy(policy);
    setIsDeleteOpen(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const url = `/api/hr/leave-policies/${selectedPolicy._id}`;
      const method = "delete";

      const res = await api({ url, method });

      if (res.data.success) {
        toast.success(res.data.message || "Leave policy deleted successfully.");
        await fetchLeavePolicies();
        setIsDeleteOpen(false);
        setSelectedPolicy(null);
      } else {
        toast.error(res.data.message || "Failed to delete policy.");
      }
    } catch (err) {
      setDeleteError({ 
        message: err.response?.data?.message || "This policy is assigned to employees and cannot be deleted." 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Policy Name",
      render: (row) => <span className="font-bold text-gray-800">{row.name}</span>,
    },
    {
      key: "weekOffDays",
      label: "Weekly Off",
      render: (row) => {
        const daysMapped = (row.weekOffDays || []).map(d => weekDays.find(wd => wd.id === d)?.label.substring(0, 3));
        return (
          <div className="flex gap-1 flex-wrap">
            {daysMapped.length > 0 ? (
              daysMapped.map((day, idx) => (
                <span key={idx} className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold">
                  {day}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-xs">No Weekoff</span>
            )}
          </div>
        );
      },
    },
    {
      key: "payrollCycleStartDay",
      label: "Payroll Start",
      render: (row) => <span className="font-semibold text-gray-700">{row.payrollCycleStartDay || 26}th of Month</span>,
    },
    {
      key: "allocations",
      label: "Allocations Count",
      render: (row) => (
        <span className="px-2 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
          {(row.allocations || []).length} Leave Types
        </span>
      ),
    },
    {
      key: "isDefault",
      label: "Global Default",
      render: (row) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${row.isDefault ? "bg-red-50 text-primary border border-red-100" : "bg-gray-50 text-gray-400"}`}>
          {row.isDefault ? "Default" : "Custom"}
        </span>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      render: (row) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${row.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${row.isActive ? "bg-green-500" : "bg-gray-400"}`} />
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View Details",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
    {
      label: "Edit Policy",
      icon: <Edit2 className="w-4 h-4" />,
      onClick: (row) => openFormDrawer(row),
    },
    {
      label: "Delete Policy",
      icon: <Trash2 className="w-4 h-4 text-red-600" />,
      onClick: (row) => triggerDelete(row),
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Leave Policies"
        description="Bundle leave types, configure payroll cycles, and assign default rules to dynamic employee tiers."
        titleIcon={Layers}
        action={{
          label: "Create Policy",
          icon: <Plus className="w-4 h-4" />,
          onClick: () => openFormDrawer(),
        }}
      />

      <div className="mt-4">
        <ModernTable
          columns={columns}
          data={policies}
          actions={tableActions}
          selectable={false}
          stickyColumns={1}
          actionType="dropdown"
        />
      </div>

      {/* RIGHT-SIDE POLICY VIEW ONLY DRAWER */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Policy Details
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Comprehensive view of configurations and limits.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                <div className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Summary</div>
                <div className="text-xl font-bold text-gray-800">{selectedPolicy?.name}</div>
                
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 mt-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>Payroll Cycle Starts: <strong>{selectedPolicy?.payrollCycleStartDay || 26}th of Month</strong></span>
                </div>

                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Week-off: <strong>
                    {(selectedPolicy?.weekOffDays || []).map(d => weekDays.find(wd => wd.id === d)?.label).join(", ") || "None"}
                  </strong></span>
                </div>

                {/* VIEW DRAWER-இல் பர்மிஷன் விவரங்களைக் காட்டுதல் */}
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 border-t border-slate-200/60 pt-2.5">
                  <Clock className="w-4 h-4 text-gray-400 animate-pulse" />
                  <span>Max Duration: <strong>{selectedPolicy?.maxPermissionMinutesPerRequest || 120} Mins per Request</strong></span>
                </div>

                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>Max Limit / Month: <strong>{selectedPolicy?.maxPermissionMinutesPerMonth || 240} Mins total</strong></span>
                </div>

                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Max Frequency / Month: <strong>
                    {selectedPolicy?.maxPermissionsPerMonth && selectedPolicy.maxPermissionsPerMonth > 0 
                      ? `${selectedPolicy.maxPermissionsPerMonth} Times` 
                      : "Unlimited"}
                  </strong></span>
                </div>

                <div className="pt-2 flex gap-2">
                  {selectedPolicy?.isDefault && (
                    <span className="bg-red-50 text-primary border border-red-100 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                      Global Default
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${selectedPolicy?.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {selectedPolicy?.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Allocations</h4>
                {(selectedPolicy?.allocations || []).length === 0 ? (
                  <div className="text-sm text-gray-400 py-3 text-center">No allocations assigned.</div>
                ) : (
                  (selectedPolicy?.allocations || []).map((alloc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3.5 bg-white border border-gray-200 rounded-xl shadow-sm">
                      <div className="flex items-center gap-3">
                        <span 
                          className="px-2.5 py-1 rounded text-xs font-bold uppercase"
                          style={{
                            backgroundColor: `${alloc.leaveTypeId?.color || "#6366f1"}15`,
                            color: alloc.leaveTypeId?.color || "#6366f1",
                            border: `1px solid ${alloc.leaveTypeId?.color || "#6366f1"}30`
                          }}
                        >
                          {alloc.leaveTypeId?.code || "LV"}
                        </span>
                        <div>
                          <div className="text-sm font-bold text-gray-800">{alloc.leaveTypeId?.name || "Custom Leave"}</div>
                          <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                            {alloc.accrualType === "monthly" ? "Monthly Accrual" : "Upfront Credit"}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-gray-900">{alloc.daysPerYear} Days</div>
                        <div className="text-[10px] font-medium text-gray-400">allocated</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 bg-primary text-white text-sm font-bold rounded-lg transition-colors hover:bg-secondary"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT-SIDE POLICY FORM DRAWER */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isFormOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeFormDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-2xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isFormOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {selectedPolicy ? "Edit Leave Policy" : "Create Leave Policy"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Bundle leave types, specify payroll cycles, and set weekends.</p>
              </div>
              <button
                type="button"
                onClick={closeFormDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* SECTION: Identity */}
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Policy Setup</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Policy Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Standard Corporate Policy"
                      className={`w-full h-10 px-3 border rounded-md text-sm focus:outline-none transition-all ${
                        formErrors.name ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Payroll Cycle Start Day *</label>
                    <input
                      type="number"
                      name="payrollCycleStartDay"
                      value={formData.payrollCycleStartDay}
                      onChange={handleInputChange}
                      placeholder="26"
                      className={`w-full h-10 px-3 border rounded-md text-sm focus:outline-none transition-all ${
                        formErrors.payrollCycleStartDay ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.payrollCycleStartDay && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.payrollCycleStartDay}</p>}
                  </div>
                </div>
              </div>

              {/* 👈 SECTION: Permission Policy Settings (புதிதாக சேர்க்கப்பட்டது) */}
              <div className="border-t border-gray-100 pt-5">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Permission Policy Settings</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Duration (Mins) *</label>
                    <input
                      type="number"
                      name="maxPermissionMinutesPerRequest"
                      value={formData.maxPermissionMinutesPerRequest}
                      onChange={handleInputChange}
                      placeholder="120"
                      className={`w-full h-10 px-3 border rounded-md text-sm focus:outline-none transition-all ${
                        formErrors.maxPermissionMinutesPerRequest ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.maxPermissionMinutesPerRequest && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.maxPermissionMinutesPerRequest}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Month Duration (Mins)</label>
                    <input
                      type="number"
                      name="maxPermissionMinutesPerMonth"
                      value={formData.maxPermissionMinutesPerMonth}
                      onChange={handleInputChange}
                      placeholder="240"
                      className={`w-full h-10 px-3 border rounded-md text-sm focus:outline-none transition-all ${
                        formErrors.maxPermissionMinutesPerMonth ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.maxPermissionMinutesPerMonth && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.maxPermissionMinutesPerMonth}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Permissions / Month</label>
                    <input
                      type="number"
                      name="maxPermissionsPerMonth"
                      value={formData.maxPermissionsPerMonth}
                      onChange={handleInputChange}
                      placeholder="4"
                      className="w-full h-10 px-3 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-blue-100 rounded-md"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Weekly Off Selection */}
              <div className="border-t border-gray-100 pt-5">
                <SelectDropdown
                  label="Weekly Off Days (Multiple Selection allowed)"
                  options={weekDays}
                  value={formData.weekOffDays}
                  onChange={(val) => handleSelectChange("weekOffDays", val)}
                  labelKey="label"
                  valueKey="id"
                  multiple={true}
                  zIndex={80}
                  showSelectedCount={false}
                />
              </div>

              {/* SECTION: Leave Type Allocations */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Type Allocations</h4>
                    <p className="text-[11px] text-gray-400">Map specific leave configurations and annual caps to this policy.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addAllocationRow}
                    className="flex items-center gap-1 text-xs bg-primary hover:bg-secondary font-bold text-white px-3 py-1.5 rounded-lg transition-all hover:opacity-90 shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Row</span>
                  </button>
                </div>

                {formErrors.allocations && (
                  <div className="flex items-start gap-2 bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-xs font-semibold text-red-800 leading-relaxed">{formErrors.allocations}</p>
                  </div>
                )}

                <div className="space-y-3">
                  {formData.allocations.length === 0 ? (
                    <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl text-gray-400 text-xs">
                      No allocations configured. Click "Add Row" to link leave types.
                    </div>
                  ) : (
                    formData.allocations.map((alloc, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end bg-slate-50/50 p-3 rounded-xl border border-gray-100 relative">
                        <div className="col-span-5">
                          <SelectDropdown
                            label="Leave Type"
                            options={leaveTypesOptions}
                            value={alloc.leaveTypeId}
                            onChange={(val) => handleAllocationChange(index, "leaveTypeId", val)}
                            labelKey="label"
                            valueKey="id"
                            placeholder="Select"
                            zIndex={70}
                          />
                        </div>

                        <div className="col-span-3">
                          <label className="block text-[11px] font-bold text-gray-700 mb-1.5 uppercase">Days / Yr</label>
                          <input
                            type="number"
                            value={alloc.daysPerYear}
                            onChange={(e) => handleAllocationChange(index, "daysPerYear", Number(e.target.value))}
                            className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
                          />
                        </div>

                        <div className="col-span-3">
                          <SelectDropdown
                            label="Accrual"
                            options={accrualTypes}
                            value={alloc.accrualType}
                            onChange={(val) => handleAllocationChange(index, "accrualType", val)}
                            labelKey="label"
                            valueKey="id"
                            zIndex={70}
                          />
                        </div>

                        <div className="col-span-1 flex justify-center pb-1">
                          <button
                            type="button"
                            onClick={() => removeAllocationRow(index)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete Row"
                          >
                            <Trash2 className="w-4.5 h-4.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* SECTION: Settings Toggles */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Advanced Settings</h4>
                
                <div className="grid grid-cols-1 gap-4 bg-slate-50/50 p-4 rounded-xl border border-gray-100">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Set as Global Default Policy</span>
                      <p className="text-[11px] text-gray-400">Newly added employees will auto-inherit this policy tier on creation.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer border-t border-gray-100 pt-3 mt-1">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Active Status</span>
                      <p className="text-[11px] text-gray-400">Determines if this policy remains selectable for employees.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>
                </div>
              </div>
            </form>

            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeFormDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm bg-primary hover:bg-secondary font-bold text-white transition-colors"
              >
                {isSubmitting ? "Saving Parameters..." : selectedPolicy ? "Update Policy" : "Create Policy"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DELETE CONFIRMATION OFF-CANVAS */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          isDeleteOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsDeleteOpen(false)}
      >
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white shadow-2xl rounded-b-2xl transform transition-transform duration-300 pb-6 px-6 pt-5 ${
            isDeleteOpen ? "translate-y-0" : "-translate-y-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 border border-red-100">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Delete Leave Policy</h2>
                <p className="text-xs text-gray-400">Please confirm if you want to delete this configuration tier.</p>
              </div>
            </div>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              onClick={() => setIsDeleteOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-5">
            {!deleteError ? (
              <div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Are you sure you want to delete the policy{" "}
                  <span className="font-bold text-gray-800">{selectedPolicy?.name}</span>? 
                  This action cannot be undone. If employees are currently bound to this policy tier, you will be blocked until they are migrated.
                </p>
                
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                    onClick={() => setIsDeleteOpen(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-primary transition-all hover:opacity-90"
                    onClick={confirmDelete}
                    disabled={isLoading}
                  >
                    {isLoading ? "Deleting..." : "Delete Policy"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl">
                <p className="text-xs font-semibold text-red-800">{deleteError.message}</p>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsDeleteOpen(false)}
                    className="bg-white text-gray-500 border border-gray-200 px-4 py-2 rounded-lg text-xs font-bold hover:bg-gray-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeavePoliciesPage;